import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Alert,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useUpdateUserProfileMutation } from "../../services/auth/authApi";
import { useSelector } from "react-redux";

const ChangePassword = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const user = useSelector((state: any) => state.auth.user);

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
    setApiError("");
  };

  const togglePasswordVisibility = (field: "current" | "new" | "confirm") => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };

    if (!formData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
      isValid = false;
    }

    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
      isValid = false;
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters long";
      isValid = false;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
      isValid = false;
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        const formDataToSend = new FormData();
        formDataToSend.append("old_password", formData.currentPassword);
        formDataToSend.append("user_id", user.id.toString());
        formDataToSend.append("email", user.email);
        formDataToSend.append("password", formData.newPassword);
        formDataToSend.append(
          "password_confirmation",
          formData.confirmPassword
        );

        const response = await updateUserProfile(formDataToSend).unwrap();

        if (response.success) {
          console.log("response", response);
          setSuccess(true);
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        } else {
          setApiError(response.error || "Failed to change password");
        }
      } catch (error: any) {
        console.log("error", error);
        setApiError(
          error.message || "An error occurred while changing password"
        );
      }
    }
  };

  return (
    <Container maxWidth="sm">
      {isMobile && (
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            position: "absolute",
            top: 32,
            left: 16,
            backgroundColor: theme.palette.background.paper,
            "&:hover": {
              backgroundColor: theme.palette.background.default,
            },
            borderRadius: "50%",
            zIndex: 1,
          }}
        >
          <Icon
            icon="material-symbols:arrow-back"
            style={{
              fontSize: "24px",
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
            }}
          />
        </IconButton>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 4,
        }}
      >
        <Typography variant="h3" fontWeight="semiBold" mb={3} mt={5}>
          Change Password
        </Typography>
        <Typography
          variant="body2"
          fontWeight="semibold"
          sx={{ fontSize: "20px" }}
          color="textSecondary"
          mb={4}
        >
          Enter your current and new password to update.
        </Typography>

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3, width: "100%" }}
            onClose={() => setSuccess(false)}
          >
            Password changed successfully!
          </Alert>
        )}

        {apiError && (
          <Alert
            severity="error"
            sx={{ mb: 3, width: "100%" }}
            onClose={() => setApiError("")}
          >
            {apiError}
          </Alert>
        )}

        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <TextField
            fullWidth
            label="Current Password"
            name="currentPassword"
            type={showPasswords.current ? "text" : "password"}
            value={formData.currentPassword}
            onChange={handleChange}
            error={!!errors.currentPassword}
            helperText={errors.currentPassword}
            variant="outlined"
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility("current")}
                    edge="end"
                  >
                    <Icon
                      icon={showPasswords.current ? "mdi:eye-off" : "mdi:eye"}
                      color={theme.palette.primary.main}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="New Password"
            name="newPassword"
            type={showPasswords.new ? "text" : "password"}
            value={formData.newPassword}
            onChange={handleChange}
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            variant="outlined"
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility("new")}
                    edge="end"
                  >
                    <Icon
                      icon={showPasswords.new ? "mdi:eye-off" : "mdi:eye"}
                      color={theme.palette.primary.main}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            name="confirmPassword"
            type={showPasswords.confirm ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={handleChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            variant="outlined"
            sx={{ mb: 4 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility("confirm")}
                    edge="end"
                  >
                    <Icon
                      icon={showPasswords.confirm ? "mdi:eye-off" : "mdi:eye"}
                      color={theme.palette.primary.main}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: "40px",
                textTransform: "capitalize",
                padding: "10px",
                height: "60px",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: theme.palette.primary.lighter,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "40px",
                textTransform: "capitalize",
                padding: "10px",
                height: "60px",
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Change Password
            </Button>
          </Box>
        </form>
      </Box>
    </Container>
  );
};

export default ChangePassword;
